import { FormattedMessage, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router'
import { useAppDispatch, useAppSelector } from 'universal-scripts'
import { useDashboardCRUD } from '@hooks/useDashboard'
import {
  useDashboardHasChanges,
  useDashboardHasTitle
} from '@hooks/useDashboardChanges'
import { showToast, updateOriginalToDraft } from 'src/store/slices/dashboard'
import { haveItemsChanged } from 'src/utils/dashboardItemsUtils'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import type { DashboardListItem } from 'src/types/dashboard'
import { useTrialGuard } from '@hooks/useTrialGuard'

export default function BuilderHeader() {
  const { id } = useParams<{ id: string }>()
  const intl = useIntl()
  const { guard } = useTrialGuard()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { createDashboard, updateDashboard, deleteDashboard } =
    useDashboardCRUD()

  const { draft, original } = useAppSelector((state) => state.dashboard)
  const hasChanges = useDashboardHasChanges()
  const hasTitle = useDashboardHasTitle()

  const isNew = id === 'new'

  const label =
    draft.title?.trim() ||
    (isNew ? intl.formatMessage({ id: 'dashboard.new' }) : (id ?? ''))

  const crumbs = [
    {
      label: intl.formatMessage({ id: 'nav.dashboards' }),
      path: '/dashboards'
    },
    {
      label,
      path: isNew ? '/dashboard-builder/new' : `/dashboard-builder/${id}`
    }
  ]

  const handleSave = async () => {
    try {
      if (isNew) {
        const response = await createDashboard({
          title: draft.title,
          public: draft.public ?? false,
          items: draft.items ?? []
        })
        if (response?.ok && response?.dashboard?.id) {
          dispatch(updateOriginalToDraft())
          navigate(`/dashboard-builder/${response.dashboard.id}`, {
            replace: true
          })
        }
      } else if (id) {
        const payload: Partial<DashboardListItem> = {}
        if (!original || draft.title !== original.title) {
          payload.title = draft.title
        }
        if (!original || draft.public !== original.public) {
          payload.public = draft.public
        }
        if (haveItemsChanged(original?.items, draft.items)) {
          payload.items = draft.items
        }
        if (Object.keys(payload).length > 0) {
          await updateDashboard(id, payload)
          dispatch(updateOriginalToDraft())
          dispatch(showToast(intl.formatMessage({ id: 'builder.alert.saved' })))
        }
      }
    } catch (error) {
      console.error('save:', error)
    }
  }

  const handleDelete = async () => {
    const isConfirmed = window.confirm(
      intl.formatMessage({ id: 'builder.confirm.delete' })
    )
    if (isConfirmed) {
      try {
        await deleteDashboard(id as string)
        navigate('/dashboards', { replace: true })
      } catch (error) {
        console.error('delete:', error)
      }
    }
  }

  const headerActions = (
    <>
      <button
        className="dashboard-button highlight-button"
        onClick={guard(handleSave)}
        disabled={!hasChanges || !hasTitle}
      >
        <span className="icon icon-diskette" />
        <span>
          <FormattedMessage id="save" />
        </span>
      </button>
      <button
        disabled={isNew}
        className="dashboard-button standart-button"
        onClick={guard(handleDelete)}
      >
        <span className="icon icon-bin" />
        <span>
          <FormattedMessage id="delete" />
        </span>
      </button>
    </>
  )

  return (
    <ExternalHeaderContent
      hasBack
      crumbs={crumbs}
      headerActions={headerActions}
    />
  )
}
